import { Badge, Button, ButtonGroup, Checkbox, Link, Text, Tooltip } from "@shopify/polaris"
import { observer } from "mobx-react"
import { useEffect, useMemo, useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import { useIntercom } from "react-use-intercom"

import { TMSMigrationMetadatum, TMSMigrationSettingItem } from "@/api/dashboard/type"
import { hasPermission } from "@/component"
import PermissionBadge from "@/component/PermissionBadge"
import Permission from "@/enums/permission"
import toast from "@/utils/toast"

import MigrateSetupGuideTabContent from "../components/MigrateSetupGuideTabContent"
import styles from "../index.module.scss"
import { MigrateSetupGuideTabProps } from "./types"

interface CoreSettingOption {
  id: string
  label: string
  hasOverlap?: boolean
  tooltipDescription?: string
  disabled?: boolean
  permission?: Permission
  settingItem?: TMSMigrationSettingItem
}

const SHIPPING_NOTIFICATIONS_SETTING_ID = "shipping-notifications"

const CORE_SETTING_OPTION_IDS = [
  "frequently-used-carriers",
  "update-tracking-link",
  "word-masking",
  "dropshipping-mode",
  "send-shipping-tracking-events",
  SHIPPING_NOTIFICATIONS_SETTING_ID,
] as const

const SETTING_ID_TO_API_KEY_MAP: Record<string, keyof TMSMigrationMetadatum> = {
  "frequently-used-carriers": "commonly_used_carriers",
  "dropshipping-mode": "dropshipping_mode",
  "update-tracking-link": "update_tracking_link",
  "send-shipping-tracking-events": "send_shipping_tracking_events",
  "word-masking": "word_masking",
  [SHIPPING_NOTIFICATIONS_SETTING_ID]: "shipping_notifications",
}

const isSettingInitiallyChecked = (settingItem?: TMSMigrationSettingItem) => {
  return Boolean(settingItem?.default && !settingItem?.should_paid)
}

const isCoreSettingOptionDisabled = (option: CoreSettingOption) => {
  const { settingItem } = option

  return Boolean(option.disabled || settingItem?.migrated || settingItem?.should_paid)
}

const CoreSettingsTab = observer(({
  tmsMigration,
  onCompleteStep,
  isCompleted,
  coreSettingsSelectedIds,
  onCoreSettingsSelectedIdsChange,
}: MigrateSetupGuideTabProps) => {
  const { t } = useTranslation(["dashboard", "common"])
  const { showNewMessage } = useIntercom()
  const hasShippingNotificationsPermission = hasPermission(Permission.ParcelWILLShippingNotifications)

  const metadata = tmsMigration?.progresses[2]?.metadata?.[0]

  const coreSettingOptions = useMemo<CoreSettingOption[]>(() => [
    {
      id: "frequently-used-carriers",
      label: t("dashboard:ImportCoreSettingsFrequentlyUsedCarriers"),
      settingItem: metadata?.commonly_used_carriers,
    },
    {
      id: "dropshipping-mode",
      label: t("dashboard:ImportCoreSettingsDropshippingMode"),
      settingItem: metadata?.dropshipping_mode,
    },
    {
      id: "update-tracking-link",
      label: t("dashboard:ImportCoreSettingsUpdateTrackingLink"),
      hasOverlap: metadata?.update_tracking_link?.overlap ?? false,
      settingItem: metadata?.update_tracking_link,
    },
    {
      id: "send-shipping-tracking-events",
      label: t("dashboard:ImportCoreSettingsSendShippingTrackingEvents"),
      hasOverlap: metadata?.send_shipping_tracking_events?.overlap ?? false,
      settingItem: metadata?.send_shipping_tracking_events,
    },
    {
      id: "word-masking",
      label: t("dashboard:ImportCoreSettingsWordMasking"),
      settingItem: metadata?.word_masking,
    },
    {
      id: SHIPPING_NOTIFICATIONS_SETTING_ID,
      label: t("dashboard:ImportCoreSettingsShippingNotifications"),
      hasOverlap: metadata?.shipping_notifications?.overlap ?? false,
      disabled: !hasShippingNotificationsPermission,
      permission: Permission.ParcelWILLShippingNotifications,
      settingItem: metadata?.shipping_notifications,
      tooltipDescription: t("dashboard:ImportCoreSettingsShippingNotificationsDescription"),
    },
  ], [hasShippingNotificationsPermission, t, metadata])

  const [submittingAction, setSubmittingAction] = useState<"import" | "manual" | null>(null)
  const isSubmitting = submittingAction !== null
  const isImporting = submittingAction === "import"
  const isManualSubmitting = submittingAction === "manual"

  // 勾选项由父组件持有，避免切换 tab 后丢失修改并被 default 重置；null 表示尚未初始化
  const selectedSettingIds = coreSettingsSelectedIds ?? []
  const hasInitializedSelection = coreSettingsSelectedIds !== null && coreSettingsSelectedIds !== undefined

  const areAllCheckboxesDisabled = coreSettingOptions.length > 0
    && coreSettingOptions.every(option => isCoreSettingOptionDisabled(option))

  // 是否存在「可导入」的勾选项：已导入(migrated)等被禁用的项虽然会勾选，但不应计入
  const hasImportableSelection = coreSettingOptions.some(
    option => !isCoreSettingOptionDisabled(option) && selectedSettingIds.includes(option.id),
  )

  // 仅在首次（尚未初始化）时按 default 预选，用户修改后不再重置
  useEffect(() => {
    if (metadata && !hasInitializedSelection) {
      const initialSelectedIds = coreSettingOptions
        .filter(option => !option.disabled && isSettingInitiallyChecked(option.settingItem))
        .map(option => option.id)

      onCoreSettingsSelectedIdsChange?.(initialSelectedIds)
    }
  }, [metadata, coreSettingOptions, hasInitializedSelection, onCoreSettingsSelectedIdsChange])

  const handleSettingChange = (option: CoreSettingOption, checked: boolean) => {
    if (isCoreSettingOptionDisabled(option)) {
      return
    }

    if (checked) {
      if (!selectedSettingIds.includes(option.id)) {
        onCoreSettingsSelectedIdsChange?.([...selectedSettingIds, option.id])
      }

      return
    }

    onCoreSettingsSelectedIdsChange?.(selectedSettingIds.filter((id) => id !== option.id))
  }

  const handleCompleteStepAction = async (
    action: "import" | "manual",
    importSettings?: Record<string, boolean>,
  ) => {
    if (!onCompleteStep || isSubmitting) {
      return
    }

    setSubmittingAction(action)

    try {
      const isSuccess = await onCompleteStep(2, importSettings)

      if (action === "import") {
        if (isSuccess) {
          toast(t("dashboard:ImportCoreSettingsImportedSuccessfully"))
        } else {
          toast(t("dashboard:ImportCoreSettingsImportFailed"), 4e3, true)
        }
      }
    } finally {
      setSubmittingAction(null)
    }
  }

  const handleImport = async () => {
    const importSettings: Record<string, boolean> = {}

    CORE_SETTING_OPTION_IDS.forEach((id) => {
      const apiKey = SETTING_ID_TO_API_KEY_MAP[id]

      importSettings[apiKey] = selectedSettingIds.includes(id)
    })

    await handleCompleteStepAction("import", importSettings)
  }

  const handleManualComplete = async () => {
    await handleCompleteStepAction("manual")
  }

  const renderOptionLabel = (option: CoreSettingOption) => {
    const labelNode = option.tooltipDescription ? (
      <Tooltip content={option.tooltipDescription} hasUnderline>
        <Text as={"span"} variant={"bodyMd"}>{option.label}</Text>
      </Tooltip>
    ) : (
      <span>{option.label}</span>
    )

    return labelNode
  }

  return (
    <MigrateSetupGuideTabContent
      header={t("dashboard:ImportCoreSettings")}
      description={t("dashboard:ImportCoreSettingsDescription")}
      media={<img src={"https://cdn.parcelpanel.com/front-end/2026/tms-migration/core-settings-import.svg"} alt={""} />}
      actions={
        <div className={"flex flex-col gap-3"}>
          <div className={styles.coreSettingsGrid}>
            {coreSettingOptions.map((option) => {
              const isDisabled = isCoreSettingOptionDisabled(option)
              const isChecked = selectedSettingIds.includes(option.id)

              return (
                <div key={option.id} className={styles.coreSettingsCheckboxItem}>
                  <Checkbox
                    label={renderOptionLabel(option)}
                    checked={isChecked}
                    disabled={isDisabled}
                    onChange={(checked) => handleSettingChange(option, checked)}
                  />
                  {option.permission && !hasPermission(option.permission) ? (
                    <PermissionBadge
                      text={"Essential"}
                      level={2}
                      permission={option.permission}
                    />
                  ) : option.hasOverlap ? (
                    <span className={styles.overlapBadge}>
                      <Badge tone={"enabled"}>{t("dashboard:ImportCoreSettingsOverlap")}</Badge>
                    </span>
                  ) : null}
                </div>
              )
            })}
          </div>

          <ButtonGroup>
            <Button
              variant={isCompleted ? "secondary" : "primary"}
              onClick={handleImport}
              disabled={areAllCheckboxesDisabled || !hasImportableSelection || isManualSubmitting}
              loading={isImporting}
            >
              {t("dashboard:ImportCoreSettingsImportButton")}
            </Button>
            {!isCompleted && (
              <Button
                variant={"tertiary"}
                onClick={handleManualComplete}
                disabled={isImporting}
                loading={isManualSubmitting}
              >
                {t("dashboard:ReplaceTrackingPageManualButton")}
              </Button>
            )}
          </ButtonGroup>
        </div>
      }
      footer={
        <Trans ns={"dashboard"} i18nKey={"NeedHelp"}>
          <Link onClick={() => showNewMessage("")} removeUnderline />
        </Trans>
      }
    />
  )
})

export default CoreSettingsTab
